import { useMemo } from 'react';
import { useHasPermissionCallback } from 'hooks/use-has-permission';
import { applyRailGroups, type RailGroup } from 'components/layout/navbar/navbar-rail-groups';
import NavbarMenuItem, { type MenuItem } from 'components/layout/navbar/navbar-menu-item';
import NavbarMenuItemSkeleton from 'components/layout/navbar/navbar-menu-item-skeleton';
import NavbarMenuSectionTitle, { type MenuSectionTitle } from 'components/layout/navbar/navbar-menu-section-title';

type ExtendedMenuItem = (MenuItem | MenuSectionTitle) & {
  requiredPermissions?: string[];
  isLoading?: boolean;
  shouldShow?: boolean;
};

interface NavbarMenuListProps {
  items: ExtendedMenuItem[];
  /**
   * The rail's grouping and order. Applied here rather than by the caller because a group
   * label's visibility depends on its members', and `shouldShow` is resolved from
   * `requiredPermissions` *below* — on the raw declarations every member still looks visible, so an
   * unauthorized group would render a lone label. Absent or empty leaves the list untouched.
   */
  groups?: RailGroup[];
}

const NavbarMenuList = ({ items, groups }: NavbarMenuListProps) => {
  const hasPermission = useHasPermissionCallback();

  const processedMenuItems = useMemo(() => {
    return items.map(item => {
      if (!item.requiredPermissions) {
        return {
          ...item,
          shouldShow: item.shouldShow !== undefined ? item.shouldShow : true,
          isLoading: item.isLoading || false
        };
      }

      const permissionStatuses = item.requiredPermissions.map(permission => hasPermission(permission as any));
      const allPermissionsLoaded = permissionStatuses.every(status => !status.isLoading);
      const isAuthorized = permissionStatuses.some(status => status.isAuthorized);

      return {
        ...item,
        shouldShow: allPermissionsLoaded && isAuthorized && (item.shouldShow !== undefined ? item.shouldShow : true),
        isLoading: !allPermissionsLoaded || item.isLoading || false
      };
    });
  }, [items, hasPermission]);

  const railItems = useMemo(
    () => (groups ? applyRailGroups(processedMenuItems, groups) : processedMenuItems),
    [groups, processedMenuItems]
  );

  return (
    <>
      {railItems.map(menuItem => {
        if (menuItem.isLoading) {
          return <NavbarMenuItemSkeleton key={`skeleton-${menuItem.key}`} />;
        }

        if (menuItem.shouldShow) {
          return 'isSectionTitle' in menuItem ? (
            <NavbarMenuSectionTitle key={menuItem.key} item={menuItem} />
          ) : (
            <NavbarMenuItem key={menuItem.key} item={menuItem} />
          );
        }

        return null;
      })}
    </>
  );
};

export default NavbarMenuList;
export type { ExtendedMenuItem };
